import type { OperationsNode } from '../types/operations.types'
import { isTeamNode } from './nodeTypes'

/**
 * Games under a node.
 *
 * A team carries its own `games_count`. A schedule usually does NOT — the
 * payload omits it — which is why the panel used to read "Not Set" for every
 * division. So a schedule's count is the sum of its team descendants, walking
 * through conferences and divisions alike.
 *
 * Sums TEAM nodes only, never intermediate schedules: a division that happened
 * to carry its own `gamesCount` alongside teams would otherwise be counted
 * twice.
 *
 * Always a number — a childless node is 0 games, not unknown.
 */
export const gamesCountForNode = (node: OperationsNode): number => {
  if (isTeamNode(node)) {
    return node.gamesCount
  }
  return node.children.reduce(
    (total, child) => total + gamesCountForNode(child),
    0
  )
}

/**
 * The Games figure shown in the detail panel's stat strip.
 *
 * The top competition carries its own `games_count` from the API, and that is
 * the number to trust: its teams' `games_count` comes back 0 even when games
 * exist (measured: 58 games on a season, 0 on every one of its teams), so the
 * team sum read 0 where the Schedule tab said 58. Every other level — the
 * divisions and conferences, which carry no count of their own — still sums
 * its teams via `gamesCountForNode`. A top competition without a count falls
 * back to that sum too.
 */
export const panelGamesCount = (
  node: OperationsNode,
  rootScheduleId: string
): number =>
  !isTeamNode(node) &&
  node.scheduleId === rootScheduleId &&
  node.gamesCount !== null
    ? node.gamesCount
    : gamesCountForNode(node)
